import { useState, useMemo, useCallback } from 'react';
import { useProductionManagement } from '@/lib/production-management-context';
import { useSprints } from '@/lib/sprint-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NotionAvatar } from '@/components/notion-avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
// @ts-ignore - recharts types are outdated
import { BarChart, Bar, XAxis, YAxis, Cell, PieChart, Pie } from 'recharts';
import { 
  RefreshCw, 
  Clock, 
  ChevronLeft, 
  ChevronRight,
  Users,
  Calendar,
  ArrowLeft,
  Timer,
  BarChart3,
  List,
  Filter,
  X,
  Check,
  ChevronsUpDown,
  FolderKanban,
  User
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTagChartColor } from '@/lib/tag-colors';
import { NotionIcon } from '@/components/icons/notion-icon';

// Week calculation helpers
function getWeekNumber(date: Date): { week: number; year: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { week: weekNo, year: d.getUTCFullYear() };
}

function getWeekDateRange(weekNumber: number, year: number): { start: Date; end: Date } {
  const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = new Date(simple);
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  const start = new Date(ISOweekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

function formatWeekLabel(week: number, year: number): string {
  return `W${week.toString().padStart(2, '0')}.${year.toString().slice(-2)}`;
}

function formatDateRange(start: Date, end: Date): string {
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startStr} - ${endStr}`;
}

function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function formatHoursDecimal(minutes: number): string {
  return (minutes / 60).toFixed(1) + 'h';
}

// Color palette for charts - using actual hex colors
const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#14b8a6', // teal
  '#6366f1', // indigo
];

export default function ProductionTimesheetsPage() {
  const { 
    resources, 
    projects,
    loadingResources, 
    loadingTimesheets,
    errorTimesheets,
    timesheets,
    refreshTimesheets,
    getTimesheetsByContributorId
  } = useProductionManagement();
  
  const { sprintTasks } = useSprints();

  const [refreshing, setRefreshing] = useState(false);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [resourceWeekOffset, setResourceWeekOffset] = useState(0);
  
  // Tab and filter state
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [filterResourceId, setFilterResourceId] = useState<string>('all');
  const [filterProjectId, setFilterProjectId] = useState<string>('all');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [filterTaskSearch, setFilterTaskSearch] = useState<string>('');
  
  // Popover open states for searchable filters
  const [resourceFilterOpen, setResourceFilterOpen] = useState(false);
  const [projectFilterOpen, setProjectFilterOpen] = useState(false);
  const [resourceSearch, setResourceSearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');

  // Create task title lookup from sprint tasks
  const taskTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    sprintTasks.forEach(task => {
      if (task.id && task.title) {
        map.set(task.id, task.title);
      }
    });
    return map;
  }, [sprintTasks]);

  // Create projectId lookup from sprint tasks
  const taskProjectMap = useMemo(() => {
    const map = new Map<string, string>();
    sprintTasks.forEach(task => {
      if (task.id && task.projectId) {
        map.set(task.id, task.projectId);
      }
    });
    return map;
  }, [sprintTasks]);

  // Helper to get task title from taskId
  const getTaskTitle = useCallback((taskId?: string, fallbackTitle?: string): string => {
    if (!taskId) return fallbackTitle || 'Untitled Task';
    return taskTitleMap.get(taskId) || fallbackTitle || 'Untitled Task';
  }, [taskTitleMap]);

  // Get current week info
  const currentWeekInfo = useMemo(() => {
    const today = new Date();
    const { week, year } = getWeekNumber(today);
    
    // Adjust week based on offset
    let targetWeek = week + currentWeekOffset;
    let targetYear = year;
    
    if (targetWeek < 1) {
      targetYear--;
      targetWeek += 52;
    } else if (targetWeek > 52) {
      targetYear++;
      targetWeek -= 52;
    }
    
    const { start, end } = getWeekDateRange(targetWeek, targetYear);
    return { week: targetWeek, year: targetYear, start, end };
  }, [currentWeekOffset]);

  // Get active resources
  const activeResources = useMemo(() => {
    return resources.filter(r => r.active || r.activeResource);
  }, [resources]);

  // Calculate timesheet stats per resource for current week
  const resourceStats = useMemo(() => {
    const stats: Record<string, { totalMinutes: number; entryCount: number; entries: any[] }> = {};
    
    activeResources.forEach(resource => {
      const resourceTimesheets = getTimesheetsByContributorId(resource.id);
      
      // Filter to current week
      const weekTimesheets = resourceTimesheets.filter(t => {
        if (!t.startTime) return false;
        const entryDate = new Date(t.startTime);
        return entryDate >= currentWeekInfo.start && entryDate <= currentWeekInfo.end;
      });
      
      const totalMinutes = weekTimesheets.reduce((sum, t) => sum + (t.duration || 0), 0);
      
      stats[resource.id] = {
        totalMinutes,
        entryCount: weekTimesheets.length,
        entries: weekTimesheets
      };
    });
    
    return stats;
  }, [activeResources, getTimesheetsByContributorId, currentWeekInfo]);

  // Overall stats for the week
  const weeklyStats = useMemo(() => {
    let totalMinutes = 0;
    let totalEntries = 0;
    let resourcesWithTime = 0;
    
    Object.values(resourceStats).forEach(stat => {
      totalMinutes += stat.totalMinutes;
      totalEntries += stat.entryCount;
      if (stat.totalMinutes > 0) resourcesWithTime++;
    });
    
    return { totalMinutes, totalEntries, resourcesWithTime };
  }, [resourceStats]);

  // Department stats for charts (current week)
  const departmentStats = useMemo(() => {
    const deptMap: Record<string, { totalMinutes: number; resourceCount: number; resources: string[] }> = {};
    
    activeResources.forEach(resource => {
      const stats = resourceStats[resource.id];
      if (!stats || stats.totalMinutes === 0) return;
      
      const departments = Array.isArray(resource.department) && resource.department.length > 0 
        ? resource.department 
        : ['Unassigned'];
      
      departments.forEach(dept => {
        if (!deptMap[dept]) {
          deptMap[dept] = { totalMinutes: 0, resourceCount: 0, resources: [] };
        }
        // Divide time equally if person belongs to multiple departments
        deptMap[dept].totalMinutes += stats.totalMinutes / departments.length;
        if (!deptMap[dept].resources.includes(resource.id)) {
          deptMap[dept].resources.push(resource.id);
          deptMap[dept].resourceCount++;
        }
      });
    });
    
    return Object.entries(deptMap)
      .map(([name, data]) => ({
        name,
        hours: Math.round((data.totalMinutes / 60) * 10) / 10,
        minutes: Math.round(data.totalMinutes),
        resourceCount: data.resourceCount,
        fill: getTagChartColor(name)
      }))
      .sort((a, b) => b.minutes - a.minutes);
  }, [activeResources, resourceStats]);

  // Project stats for charts (current week)
  const projectStats = useMemo(() => {
    const projectMap: Record<string, { totalMinutes: number; entryCount: number; projectName: string }> = {};
    
    // Get all timesheets for current week
    Object.values(resourceStats).forEach(stat => {
      stat.entries.forEach(entry => {
        const projectId = entry.taskId ? taskProjectMap.get(entry.taskId) : null;
        const projectName = projectId 
          ? (projects.find(p => p.id === projectId)?.name || 'Unknown Project')
          : 'No Project';
        const key = projectId || 'no-project';
        
        if (!projectMap[key]) {
          projectMap[key] = { totalMinutes: 0, entryCount: 0, projectName };
        }
        projectMap[key].totalMinutes += entry.duration || 0;
        projectMap[key].entryCount++;
      });
    });
    
    return Object.entries(projectMap)
      .map(([id, data], index) => ({
        id,
        name: data.projectName,
        hours: Math.round((data.totalMinutes / 60) * 10) / 10,
        minutes: Math.round(data.totalMinutes),
        entryCount: data.entryCount,
        fill: CHART_COLORS[index % CHART_COLORS.length]
      }))
      .sort((a, b) => b.minutes - a.minutes);
  }, [resourceStats, taskProjectMap, projects]);

  // Team member stats for charts (current week)
  const memberStats = useMemo(() => {
    return activeResources
      .map((resource, index) => {
        const stats = resourceStats[resource.id] || { totalMinutes: 0, entryCount: 0 };
        return {
          id: resource.id,
          name: resource.name,
          hours: Math.round((stats.totalMinutes / 60) * 10) / 10,
          minutes: stats.totalMinutes,
          entryCount: stats.entryCount,
          fill: CHART_COLORS[index % CHART_COLORS.length]
        };
      })
      .filter(m => m.minutes > 0)
      .sort((a, b) => b.minutes - a.minutes);
  }, [activeResources, resourceStats]);

  // Filtered entries for all entries view
  const filteredEntries = useMemo(() => {
    let entries = [...timesheets];
    
    // Filter by resource
    if (filterResourceId && filterResourceId !== 'all') {
      entries = entries.filter(t => t.contributors?.includes(filterResourceId));
    }
    
    // Filter by project (via task's projectId)
    if (filterProjectId && filterProjectId !== 'all') {
      entries = entries.filter(t => {
        if (!t.taskId) return false;
        const taskProjectId = taskProjectMap.get(t.taskId);
        return taskProjectId === filterProjectId;
      });
    }
    
    // Filter by date range
    if (filterDateFrom) {
      const fromDate = new Date(filterDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      entries = entries.filter(t => {
        if (!t.startTime) return false;
        return new Date(t.startTime) >= fromDate;
      });
    }
    
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      entries = entries.filter(t => {
        if (!t.startTime) return false;
        return new Date(t.startTime) <= toDate;
      });
    }
    
    // Filter by task name search
    if (filterTaskSearch.trim()) {
      const search = filterTaskSearch.toLowerCase();
      entries = entries.filter(t => {
        const title = getTaskTitle(t.taskId, t.taskTitle).toLowerCase();
        return title.includes(search);
      });
    }
    
    // Sort by date descending
    return entries.sort((a, b) => {
      const dateA = a.startTime ? new Date(a.startTime).getTime() : 0;
      const dateB = b.startTime ? new Date(b.startTime).getTime() : 0;
      return dateB - dateA;
    });
  }, [timesheets, filterResourceId, filterProjectId, filterDateFrom, filterDateTo, filterTaskSearch, taskProjectMap, getTaskTitle]);

  // Group filtered entries by week
  const entriesByWeek = useMemo(() => {
    const weekMap: Record<string, { 
      weekLabel: string; 
      weekKey: string;
      weekNumber: number;
      year: number;
      totalMinutes: number;
      entries: typeof filteredEntries;
    }> = {};
    
    filteredEntries.forEach(entry => {
      if (!entry.startTime) return;
      
      const date = new Date(entry.startTime);
      const { week, year } = getWeekNumber(date);
      const weekKey = `${year}-W${week.toString().padStart(2, '0')}`;
      const weekLabel = `W${week.toString().padStart(2, '0')}.${year.toString().slice(-2)}`;
      
      if (!weekMap[weekKey]) {
        weekMap[weekKey] = {
          weekLabel,
          weekKey,
          weekNumber: week,
          year,
          totalMinutes: 0,
          entries: []
        };
      }
      
      weekMap[weekKey].totalMinutes += entry.duration || 0;
      weekMap[weekKey].entries.push(entry);
    });
    
    // Sort weeks descending (most recent first)
    return Object.values(weekMap).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.weekNumber - a.weekNumber;
    });
  }, [filteredEntries]);

  // Get resource name by id
  const getResourceName = useCallback((resourceId: string): string => {
    const resource = resources.find(r => r.id === resourceId);
    return resource?.name || 'Unknown';
  }, [resources]);

  // Get project name by id
  const getProjectName = useCallback((projectId?: string): string => {
    if (!projectId) return 'No Project';
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  }, [projects]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilterResourceId('all');
    setFilterProjectId('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterTaskSearch('');
  }, []);

  const hasActiveFilters = filterResourceId !== 'all' || filterProjectId !== 'all' || filterDateFrom || filterDateTo || filterTaskSearch;

  // Selected resource details
  const selectedResource = useMemo(() => {
    if (!selectedResourceId) return null;
    return resources.find(r => r.id === selectedResourceId);
  }, [selectedResourceId, resources]);

  // Get resource week info for dialog
  const resourceWeekInfo = useMemo(() => {
    const today = new Date();
    const { week, year } = getWeekNumber(today);
    
    let targetWeek = week + resourceWeekOffset;
    let targetYear = year;
    
    if (targetWeek < 1) {
      targetYear--;
      targetWeek += 52;
    } else if (targetWeek > 52) {
      targetYear++;
      targetWeek -= 52;
    }
    
    const { start, end } = getWeekDateRange(targetWeek, targetYear);
    return { week: targetWeek, year: targetYear, start, end };
  }, [resourceWeekOffset]);

  // Get timesheets for selected resource in resource week view
  const selectedResourceTimesheets = useMemo(() => {
    if (!selectedResourceId) return [];
    
    const resourceTimesheets = getTimesheetsByContributorId(selectedResourceId);
    
    return resourceTimesheets
      .filter(t => {
        if (!t.startTime) return false;
        const entryDate = new Date(t.startTime);
        return entryDate >= resourceWeekInfo.start && entryDate <= resourceWeekInfo.end;
      })
      .sort((a, b) => {
        const dateA = a.startTime ? new Date(a.startTime).getTime() : 0;
        const dateB = b.startTime ? new Date(b.startTime).getTime() : 0;
        return dateB - dateA;
      });
  }, [selectedResourceId, getTimesheetsByContributorId, resourceWeekInfo]);

  // Group timesheets by day for the selected resource
  const timesheetsByDay = useMemo(() => {
    const grouped: Record<string, { entries: any[]; totalMinutes: number }> = {};
    
    selectedResourceTimesheets.forEach(entry => {
      if (!entry.startTime) return;
      const date = new Date(entry.startTime);
      const dayKey = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
      
      if (!grouped[dayKey]) {
        grouped[dayKey] = { entries: [], totalMinutes: 0 };
      }
      grouped[dayKey].entries.push(entry);
      grouped[dayKey].totalMinutes += entry.duration || 0;
    });
    
    return grouped;
  }, [selectedResourceTimesheets]);

  // Daily stats for selected resource graph
  const selectedResourceDailyStats = useMemo(() => {
    if (!selectedResourceId) return [];
    
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const dailyMap: Record<string, number> = {};
    
    // Initialize all days
    days.forEach(day => { dailyMap[day] = 0; });
    
    selectedResourceTimesheets.forEach(entry => {
      if (!entry.startTime) return;
      const date = new Date(entry.startTime);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      dailyMap[dayName] = (dailyMap[dayName] || 0) + (entry.duration || 0);
    });
    
    return days.map((day, index) => ({
      day,
      hours: Math.round((dailyMap[day] / 60) * 10) / 10,
      minutes: dailyMap[day],
      fill: CHART_COLORS[index % CHART_COLORS.length]
    }));
  }, [selectedResourceId, selectedResourceTimesheets]);

  // Project stats for selected resource
  const selectedResourceProjectStats = useMemo(() => {
    if (!selectedResourceId) return [];
    
    const projectMap: Record<string, { totalMinutes: number; projectName: string }> = {};
    
    selectedResourceTimesheets.forEach(entry => {
      const projectId = entry.taskId ? taskProjectMap.get(entry.taskId) : null;
      const projectName = projectId 
        ? (projects.find(p => p.id === projectId)?.name || 'Unknown Project')
        : 'No Project';
      const key = projectId || 'no-project';
      
      if (!projectMap[key]) {
        projectMap[key] = { totalMinutes: 0, projectName };
      }
      projectMap[key].totalMinutes += entry.duration || 0;
    });
    
    return Object.entries(projectMap)
      .map(([id, data], index) => ({
        id,
        name: data.projectName,
        hours: Math.round((data.totalMinutes / 60) * 10) / 10,
        minutes: data.totalMinutes,
        fill: CHART_COLORS[index % CHART_COLORS.length]
      }))
      .sort((a, b) => b.minutes - a.minutes);
  }, [selectedResourceId, selectedResourceTimesheets, taskProjectMap, projects]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshTimesheets(true);
    setRefreshing(false);
  };

  const handleResourceClick = (resourceId: string) => {
    setSelectedResourceId(resourceId);
    setResourceWeekOffset(currentWeekOffset); // Start at same week as main view
  };

  const handleCloseResourceDialog = () => {
    setSelectedResourceId(null);
    setResourceWeekOffset(0);
  };

  const loading = loadingResources || loadingTimesheets;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Timesheets Overview
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track time logged by all team members
            </p>
          </div>
          <Button 
            onClick={handleRefresh} 
            disabled={refreshing}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error State */}
        {errorTimesheets && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{errorTimesheets}</AlertDescription>
          </Alert>
        )}

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="departments" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Departments
            </TabsTrigger>
            <TabsTrigger value="entries" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              All Entries
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            {/* Week Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentWeekOffset(prev => prev - 1)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatWeekLabel(currentWeekInfo.week, currentWeekInfo.year)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDateRange(currentWeekInfo.start, currentWeekInfo.end)}
                </div>
                {currentWeekOffset !== 0 && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="mt-1 h-auto p-0"
                    onClick={() => setCurrentWeekOffset(0)}
                  >
                    Go to Current Week
                  </Button>
                )}
                {currentWeekOffset === 0 && (
                  <Badge variant="secondary" className="mt-1">Current Week</Badge>
                )}
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentWeekOffset(prev => prev + 1)}
                disabled={currentWeekOffset >= 0}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

        {/* Weekly Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Total Time Logged
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatHoursDecimal(weeklyStats.totalMinutes)}
              </div>
              <p className="text-sm text-muted-foreground">
                {formatMinutes(weeklyStats.totalMinutes)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Timer className="h-4 w-4" />
                Time Entries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{weeklyStats.totalEntries}</div>
              <p className="text-sm text-muted-foreground">
                entries this week
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Active Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{weeklyStats.resourcesWithTime}</div>
              <p className="text-sm text-muted-foreground">
                of {activeResources.length} tracked time
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner className="h-8 w-8" />
            <span className="ml-3 text-muted-foreground">Loading timesheets...</span>
          </div>
        )}

        {/* Resource Cards Grid */}
        {!loading && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Time by Team Member
            </h2>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {activeResources.map(resource => {
                const stats = resourceStats[resource.id] || { totalMinutes: 0, entryCount: 0 };
                const weeklyCapacity = typeof resource.weeklyCapacity === 'number' 
                  ? resource.weeklyCapacity 
                  : parseFloat(resource.weeklyCapacity || '40');
                const capacityMinutes = weeklyCapacity * 60;
                const utilizationPercent = capacityMinutes > 0 
                  ? Math.round((stats.totalMinutes / capacityMinutes) * 100) 
                  : 0;
                
                return (
                  <Card 
                    key={resource.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleResourceClick(resource.id)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <NotionAvatar
                          name={resource.name}
                          className="h-12 w-12"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {resource.name}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate">
                            {resource.email}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Time this week</span>
                          <span className="font-semibold">
                            {formatHoursDecimal(stats.totalMinutes)}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Entries</span>
                          <span className="text-sm">{stats.entryCount}</span>
                        </div>
                        
                        {/* Utilization Bar */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Utilization</span>
                            <span className={`text-xs font-semibold ${
                              utilizationPercent > 100 ? 'text-red-600' :
                              utilizationPercent >= 80 ? 'text-green-600' :
                              utilizationPercent >= 50 ? 'text-yellow-600' :
                              'text-gray-600'
                            }`}>
                              {utilizationPercent}%
                            </span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                utilizationPercent > 100 ? 'bg-red-500' :
                                utilizationPercent >= 80 ? 'bg-green-500' :
                                utilizationPercent >= 50 ? 'bg-yellow-500' :
                                'bg-gray-400'
                              }`}
                              style={{ width: `${Math.min(100, utilizationPercent)}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            of {weeklyCapacity}h capacity
                          </p>
                        </div>
                        
                        {Array.isArray(resource.department) && resource.department.length > 0 && (
                          <div className="flex gap-1 flex-wrap pt-2">
                            {resource.department.map(dept => (
                              <Badge key={dept} variant="secondary" className="text-xs">
                                {dept}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {activeResources.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-500">No active resources found</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments" className="mt-6">
            {/* Week Navigation for Departments */}
            <div className="flex items-center justify-between mb-6">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentWeekOffset(prev => prev - 1)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatWeekLabel(currentWeekInfo.week, currentWeekInfo.year)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDateRange(currentWeekInfo.start, currentWeekInfo.end)}
                </div>
                {currentWeekOffset !== 0 && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="mt-1 h-auto p-0"
                    onClick={() => setCurrentWeekOffset(0)}
                  >
                    Go to Current Week
                  </Button>
                )}
                {currentWeekOffset === 0 && (
                  <Badge variant="secondary" className="mt-1">Current Week</Badge>
                )}
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentWeekOffset(prev => prev + 1)}
                disabled={currentWeekOffset >= 0}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner className="h-8 w-8" />
                <span className="ml-3 text-muted-foreground">Loading department data...</span>
              </div>
            ) : departmentStats.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-500">No time logged this week</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Bar Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Hours by Department
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={departmentStats.reduce((acc, dept) => ({
                        ...acc,
                        [dept.name]: { label: dept.name, color: dept.fill }
                      }), {})}
                      className="h-[300px]"
                    >
                      <BarChart data={departmentStats} layout="vertical">
                        <XAxis type="number" tickFormatter={(val: number) => `${val}h`} />
                        <YAxis 
                          type="category" 
                          dataKey="name" 
                          width={120}
                          tick={{ fontSize: 12 }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent />}
                          formatter={(value: number) => [`${value}h`, 'Hours']}
                        />
                        <Bar dataKey="hours" radius={4}>
                          {departmentStats.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ChartContainer>
                  </CardContent>
                </Card>

                {/* Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Time Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={departmentStats.reduce((acc, dept) => ({
                        ...acc,
                        [dept.name]: { label: dept.name, color: dept.fill }
                      }), {})}
                      className="h-[300px]"
                    >
                      <PieChart>
                        <Pie
                          data={departmentStats}
                          dataKey="hours"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }: { name: string; percent: number }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {departmentStats.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <ChartTooltip 
                          content={<ChartTooltipContent />}
                          formatter={(value: number) => [`${value}h`, 'Hours']}
                        />
                      </PieChart>
                    </ChartContainer>
                  </CardContent>
                </Card>

                {/* Department Stats List */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Department Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {departmentStats.map((dept) => (
                        <div 
                          key={dept.name}
                          className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800"
                        >
                          <div 
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: dept.fill }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{dept.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {dept.resourceCount} {dept.resourceCount === 1 ? 'member' : 'members'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{dept.hours}h</p>
                            <p className="text-xs text-muted-foreground">
                              {formatMinutes(dept.minutes)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Per Project Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FolderKanban className="h-5 w-5" />
                      Hours by Project
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {projectStats.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No project data for this week
                      </div>
                    ) : (
                      <ChartContainer
                        config={projectStats.reduce((acc, proj) => ({
                          ...acc,
                          [proj.name]: { label: proj.name, color: proj.fill }
                        }), {})}
                        className="h-[300px]"
                      >
                        <BarChart data={projectStats} layout="vertical">
                          <XAxis type="number" tickFormatter={(val: number) => `${val}h`} />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            width={150}
                            tick={{ fontSize: 11 }}
                          />
                          <ChartTooltip 
                            content={<ChartTooltipContent />}
                            formatter={(value: number) => [`${value}h`, 'Hours']}
                          />
                          <Bar dataKey="hours" radius={4}>
                            {projectStats.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ChartContainer>
                    )}
                  </CardContent>
                </Card>

                {/* Per Team Member Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Hours by Team Member
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {memberStats.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No member data for this week
                      </div>
                    ) : (
                      <ChartContainer
                        config={memberStats.reduce((acc, member) => ({
                          ...acc,
                          [member.name]: { label: member.name, color: member.fill }
                        }), {})}
                        className="h-[300px]"
                      >
                        <BarChart data={memberStats} layout="vertical">
                          <XAxis type="number" tickFormatter={(val: number) => `${val}h`} />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            width={120}
                            tick={{ fontSize: 11 }}
                          />
                          <ChartTooltip 
                            content={<ChartTooltipContent />}
                            formatter={(value: number) => [`${value}h`, 'Hours']}
                          />
                          <Bar dataKey="hours" radius={4}>
                            {memberStats.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ChartContainer>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* All Entries Tab */}
          <TabsContent value="entries" className="mt-6">
            {/* Filters */}
            <Card className="mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Filter className="h-4 w-4" />
                    Filters
                  </CardTitle>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="h-4 w-4 mr-1" />
                      Clear all
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                  {/* Resource Filter - Searchable */}
                  <div className="space-y-2">
                    <Label>Resource</Label>
                    <Popover open={resourceFilterOpen} onOpenChange={(open) => {
                      setResourceFilterOpen(open);
                      if (!open) setResourceSearch('');
                    }}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={resourceFilterOpen}
                          className="w-full justify-between font-normal"
                        >
                          {filterResourceId === 'all' 
                            ? 'All resources' 
                            : activeResources.find(r => r.id === filterResourceId)?.name || 'Select resource'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-0">
                        <div className="flex items-center border-b px-3">
                          <User className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                          <input
                            className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                            placeholder="Search resources..."
                            value={resourceSearch}
                            onChange={(e) => setResourceSearch(e.target.value)}
                          />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto p-1">
                          <div
                            className={cn(
                              "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                              filterResourceId === 'all' && "bg-accent"
                            )}
                            onClick={() => {
                              setFilterResourceId('all');
                              setResourceFilterOpen(false);
                              setResourceSearch('');
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", filterResourceId === 'all' ? "opacity-100" : "opacity-0")} />
                            All resources
                          </div>
                          {activeResources
                            .filter(r => r.name && r.name.toLowerCase().includes(resourceSearch.toLowerCase()))
                            .map(r => (
                              <div
                                key={r.id}
                                className={cn(
                                  "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                                  filterResourceId === r.id && "bg-accent"
                                )}
                                onClick={() => {
                                  setFilterResourceId(r.id);
                                  setResourceFilterOpen(false);
                                  setResourceSearch('');
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", filterResourceId === r.id ? "opacity-100" : "opacity-0")} />
                                {r.name}
                              </div>
                            ))}
                          {activeResources.filter(r => r.name && r.name.toLowerCase().includes(resourceSearch.toLowerCase())).length === 0 && resourceSearch && (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              No resource found.
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Project Filter - Searchable */}
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Popover open={projectFilterOpen} onOpenChange={(open) => {
                      setProjectFilterOpen(open);
                      if (!open) setProjectSearch('');
                    }}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={projectFilterOpen}
                          className="w-full justify-between font-normal"
                        >
                          {filterProjectId === 'all' 
                            ? 'All projects' 
                            : projects.find(p => p.id === filterProjectId)?.name || 'Select project'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-0">
                        <div className="flex items-center border-b px-3">
                          <FolderKanban className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                          <input
                            className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                            placeholder="Search projects..."
                            value={projectSearch}
                            onChange={(e) => setProjectSearch(e.target.value)}
                          />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto p-1">
                          <div
                            className={cn(
                              "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                              filterProjectId === 'all' && "bg-accent"
                            )}
                            onClick={() => {
                              setFilterProjectId('all');
                              setProjectFilterOpen(false);
                              setProjectSearch('');
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", filterProjectId === 'all' ? "opacity-100" : "opacity-0")} />
                            All projects
                          </div>
                          {projects
                            .filter(p => p.name && p.name.toLowerCase().includes(projectSearch.toLowerCase()))
                            .map(p => (
                              <div
                                key={p.id}
                                className={cn(
                                  "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                                  filterProjectId === p.id && "bg-accent"
                                )}
                                onClick={() => {
                                  setFilterProjectId(p.id);
                                  setProjectFilterOpen(false);
                                  setProjectSearch('');
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", filterProjectId === p.id ? "opacity-100" : "opacity-0")} />
                                {p.name}
                              </div>
                            ))}
                          {projects.filter(p => p.name && p.name.toLowerCase().includes(projectSearch.toLowerCase())).length === 0 && projectSearch && (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              No project found.
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Date From */}
                  <div className="space-y-2">
                    <Label>From Date</Label>
                    <Input 
                      type="date" 
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                  </div>

                  {/* Date To */}
                  <div className="space-y-2">
                    <Label>To Date</Label>
                    <Input 
                      type="date" 
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                  </div>

                  {/* Task Search */}
                  <div className="space-y-2">
                    <Label>Task Search</Label>
                    <Input 
                      type="text" 
                      placeholder="Search tasks..."
                      value={filterTaskSearch}
                      onChange={(e) => setFilterTaskSearch(e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results Summary */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                Showing {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
                {hasActiveFilters && ' (filtered)'}
              </p>
              <div className="text-sm font-medium">
                Total: {formatHoursDecimal(filteredEntries.reduce((sum, e) => sum + (e.duration || 0), 0))}
              </div>
            </div>

            {/* Entries Grouped by Week */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner className="h-8 w-8" />
                <span className="ml-3 text-muted-foreground">Loading entries...</span>
              </div>
            ) : filteredEntries.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <List className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-500">
                    {hasActiveFilters ? 'No entries match your filters' : 'No time entries found'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {entriesByWeek.map((weekData) => (
                  <Card key={weekData.weekKey}>
                    {/* Week Header */}
                    <CardHeader className="py-3 px-4 bg-gray-50 dark:bg-gray-800/50 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-base font-bold px-3 py-1">
                            {weekData.weekLabel}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {weekData.entries.length} {weekData.entries.length === 1 ? 'entry' : 'entries'}
                          </span>
                        </div>
                        <div className="text-sm font-semibold">
                          {formatHoursDecimal(weekData.totalMinutes)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50/50 dark:bg-gray-800/30 border-b">
                            <tr>
                              <th className="text-left p-3 font-medium text-xs text-muted-foreground">Date</th>
                              <th className="text-left p-3 font-medium text-xs text-muted-foreground">Resource</th>
                              <th className="text-left p-3 font-medium text-xs text-muted-foreground">Task</th>
                              <th className="text-left p-3 font-medium text-xs text-muted-foreground">Project</th>
                              <th className="text-right p-3 font-medium text-xs text-muted-foreground">Duration</th>
                              <th className="text-right p-3 font-medium text-xs text-muted-foreground">Time</th>
                              <th className="text-center p-3 font-medium text-xs text-muted-foreground w-10"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {weekData.entries.map((entry) => {
                              const projectId = entry.taskId ? taskProjectMap.get(entry.taskId) : undefined;
                              const contributorName = entry.contributors?.[0] 
                                ? getResourceName(entry.contributors[0])
                                : 'Unknown';
                              
                              return (
                                <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                  <td className="p-3 text-sm">
                                    {entry.startTime 
                                      ? new Date(entry.startTime).toLocaleDateString('en-US', {
                                          weekday: 'short',
                                          month: 'short',
                                          day: 'numeric'
                                        })
                                      : '-'}
                                  </td>
                                  <td className="p-3">
                                    <div className="flex items-center gap-2">
                                      <NotionAvatar name={contributorName} className="h-6 w-6" />
                                      <span className="text-sm">{contributorName}</span>
                                    </div>
                                  </td>
                                  <td className="p-3 text-sm max-w-[200px] truncate">
                                    {getTaskTitle(entry.taskId, entry.taskTitle)}
                                  </td>
                                  <td className="p-3 text-sm text-muted-foreground max-w-[150px] truncate">
                                    {getProjectName(projectId)}
                                  </td>
                                  <td className="p-3 text-sm text-right font-medium">
                                    {formatMinutes(entry.duration || 0)}
                                  </td>
                                  <td className="p-3 text-sm text-right text-muted-foreground">
                                    {entry.startTime && entry.endTime ? (
                                      <>
                                        {new Date(entry.startTime).toLocaleTimeString('en-US', { 
                                          hour: '2-digit', minute: '2-digit' 
                                        })}
                                        {' - '}
                                        {new Date(entry.endTime).toLocaleTimeString('en-US', { 
                                          hour: '2-digit', minute: '2-digit' 
                                        })}
                                      </>
                                    ) : '-'}
                                  </td>
                                  <td className="p-3 text-center">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7"
                                          onClick={() => window.open(`https://www.notion.so/${entry.id.replace(/-/g, '')}`, '_blank')}
                                        >
                                          <NotionIcon size={14} />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>View in Notion</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Resource Detail Dialog */}
        <Dialog open={!!selectedResourceId} onOpenChange={(open) => !open && handleCloseResourceDialog()}>
          <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseResourceDialog}
                  className="p-0 h-auto"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                {selectedResource && (
                  <div className="flex items-center gap-3">
                    <NotionAvatar
                      name={selectedResource.name}
                      className="h-10 w-10"
                    />
                    <div>
                      <DialogTitle>{selectedResource.name}</DialogTitle>
                      <p className="text-sm text-muted-foreground">{selectedResource.email}</p>
                    </div>
                  </div>
                )}
              </div>
            </DialogHeader>

            {/* Week Navigation in Dialog */}
            <div className="flex items-center justify-between py-4 border-b">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setResourceWeekOffset(prev => prev - 1)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              <div className="text-center">
                <div className="font-semibold">
                  {formatWeekLabel(resourceWeekInfo.week, resourceWeekInfo.year)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatDateRange(resourceWeekInfo.start, resourceWeekInfo.end)}
                </div>
                {resourceWeekOffset !== 0 && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="mt-1 h-auto p-0"
                    onClick={() => setResourceWeekOffset(0)}
                  >
                    Go to Current Week
                  </Button>
                )}
                {resourceWeekOffset === 0 && (
                  <Badge variant="secondary" className="mt-1">Current Week</Badge>
                )}
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setResourceWeekOffset(prev => prev + 1)}
                disabled={resourceWeekOffset >= 0}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Weekly Summary */}
            <div className="py-4 border-b">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total time this week</span>
                <span className="text-xl font-bold">
                  {formatHoursDecimal(selectedResourceTimesheets.reduce((sum, t) => sum + (t.duration || 0), 0))}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-muted-foreground">Entries</span>
                <span className="text-sm">{selectedResourceTimesheets.length}</span>
              </div>
            </div>

            {/* Resource Graphs */}
            {selectedResourceTimesheets.length > 0 && (
              <div className="py-4 border-b space-y-4">
                {/* Daily Bar Chart */}
                <div>
                  <h4 className="font-medium text-sm mb-3 text-gray-900 dark:text-gray-100">Hours by Day</h4>
                  <ChartContainer
                    config={selectedResourceDailyStats.reduce((acc, day) => ({
                      ...acc,
                      [day.day]: { label: day.day, color: day.fill }
                    }), {})}
                    className="h-[150px]"
                  >
                    <BarChart data={selectedResourceDailyStats}>
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(val: number) => `${val}h`} tick={{ fontSize: 11 }} />
                      <ChartTooltip 
                        content={<ChartTooltipContent />}
                        formatter={(value: number) => [`${value}h`, 'Hours']}
                      />
                      <Bar dataKey="hours" radius={4}>
                        {selectedResourceDailyStats.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                </div>

                {/* Project Distribution */}
                {selectedResourceProjectStats.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-3 text-gray-900 dark:text-gray-100">Time by Project</h4>
                    <div className="space-y-2">
                      {selectedResourceProjectStats.map((proj) => {
                        const totalMins = selectedResourceTimesheets.reduce((sum, t) => sum + (t.duration || 0), 0);
                        const percent = totalMins > 0 ? Math.round((proj.minutes / totalMins) * 100) : 0;
                        return (
                          <div key={proj.id} className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: proj.fill }}
                            />
                            <span className="flex-1 text-sm truncate">{proj.name}</span>
                            <span className="text-sm font-medium">{proj.hours}h</span>
                            <span className="text-xs text-muted-foreground w-10 text-right">{percent}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Timesheets by Day */}
            <div className="space-y-4 py-4">
              {Object.keys(timesheetsByDay).length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-muted-foreground">No time entries for this week</p>
                </div>
              ) : (
                Object.entries(timesheetsByDay).map(([day, { entries, totalMinutes }]) => (
                  <div key={day} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{day}</h4>
                      <Badge variant="secondary">{formatMinutes(totalMinutes)}</Badge>
                    </div>
                    <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                      {entries.map((entry: any) => (
                        <div 
                          key={entry.id}
                          className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-sm">
                                {getTaskTitle(entry.taskId, entry.taskTitle)}
                              </p>
                              {entry.projectName && (
                                <p className="text-xs text-muted-foreground">
                                  {entry.projectName}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-sm">
                                {formatMinutes(entry.duration || 0)}
                              </p>
                              {entry.startTime && entry.endTime && (
                                <p className="text-xs text-muted-foreground">
                                  {new Date(entry.startTime).toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                  {' - '}
                                  {new Date(entry.endTime).toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
